import { FC, useEffect, useState } from "react";
import {
  Modal,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Button,
  notification,
  Tooltip,
} from "antd";
import moment from "moment";
import API from "../../../services/api.service";
import { APIS } from "../../../constants/apis";
import DayTypeLegend from "../../DayTypeLegend";
import {
  villaDayClass,
  villaDayIndex,
  EMPTY_VILLA_DAYS,
  VillaDayIndex,
  specialDayLabel,
} from "../../../utils/dayTypes";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

// Admin "book on behalf of an owner". Sends owner_id so the backend creates the
// booking AS that owner (manager-gated) and validates against ALL their rules.
const NewBooking: FC<Props> = ({ open, onClose, onCreated }) => {
  const [owners, setOwners] = useState<any[]>([]);
  const [villas, setVillas] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const [owner, setOwner] = useState<number | undefined>(undefined);
  const [villa, setVilla] = useState<number | undefined>(undefined);
  // Days the chosen villa is already unavailable, as YYYY-MM-DD. Kept separate
  // (booked vs maintenance) so the picker can mark each with its own style +
  // hover tooltip, matching the edit-booking picker.
  const [bookedDates, setBookedDates] = useState<Set<string>>(new Set());

  // Peak / high-peak days come from the chosen villa's calendar rather than the
  // global special-days list: the API has already applied the long-weekend and
  // 14-day-decay rules there, so the picker shows exactly what the app shows.
  const [villaDays, setVillaDays] =
    useState<VillaDayIndex>(EMPTY_VILLA_DAYS);
  const [maintDates, setMaintDates] = useState<Set<string>>(new Set());
  const [start, setStart] = useState<string>("");
  const [end, setEnd] = useState<string>("");
  // Check-in / arrival time (the app's "Time of arrival"). Check-out is fixed at
  // 11:00 AM — every booking in the system checks out at 11:00.
  const [arrival, setArrival] = useState<any>(moment("12:00", "HH:mm"));
  const [bookingFor, setBookingFor] = useState<string>("myself");
  const [gName, setGName] = useState<string>("");
  const [gMobile, setGMobile] = useState<string>("");
  const [gEmail, setGEmail] = useState<string>("");
  const [adults, setAdults] = useState<number>(1);
  const [kids, setKids] = useState<number>(0);
  const [seniors, setSeniors] = useState<number>(0);
  const [infants, setInfants] = useState<number>(0);
  const [pets, setPets] = useState<number>(0);
  // Optional admin note, the same field the edit drawer writes. Shown to the
  // owner on the app's booking details screen.
  const [note, setNote] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const o = await API.post(APIS.HOME.OWNER_LIST, {
          role: "MANAGER",
          limit: 1000,
        });
        if (o.data.status === 1) setOwners(o.data.data || []);
      } catch (e) {
        // silent
      }
    })();
  }, [open]);

  // Scope the villa list to the picked owner: an owner can only be booked into
  // the villas they actually own. Reset the villa whenever the owner changes.
  useEffect(() => {
    setVilla(undefined);
    if (!owner) {
      setVillas([]);
      return;
    }
    (async () => {
      try {
        const res = await API.get(APIS.HOME.OWNER_PROPERTIES + owner);
        setVillas(res.data.status === 1 ? res.data.data || [] : []);
      } catch (e) {
        setVillas([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  // When a villa is picked, pull its calendar and block its unavailable days
  // (already-booked + maintenance) in the date picker. Peak / high-peak days
  // stay selectable — those are just special-rate days, still bookable. Also
  // clears any chosen dates when the villa changes.
  useEffect(() => {
    setStart("");
    setEnd("");
    if (!villa) {
      setBookedDates(new Set());
      setMaintDates(new Set());
      setVillaDays(EMPTY_VILLA_DAYS);
      return;
    }
    (async () => {
      try {
        const res = await API.get(APIS.BOOKING.CALENDAR + villa);
        const d = res.data?.data || {};
        const toDay = (s: string) => String(s).slice(0, 10); // YYYY-MM-DD
        setBookedDates(new Set<string>((d.booked_date || []).map(toDay)));
        setMaintDates(
          new Set<string>((d.maintenanceday_dates || []).map(toDay))
        );
        setVillaDays(villaDayIndex(d));
      } catch (e) {
        setBookedDates(new Set());
        setMaintDates(new Set());
        setVillaDays(EMPTY_VILLA_DAYS);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villa]);

  // Today is bookable, but only at a time that hasn't gone by yet. If the start
  // date is today and the chosen arrival is already behind us, snap it forward
  // so the form can never submit a check-in in the past.
  const isToday = !!start && start === moment().format("YYYY-MM-DD");
  useEffect(() => {
    if (!isToday) return;
    if (arrival && arrival.isBefore(moment(), "minute")) {
      setArrival(moment().startOf("minute"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, start]);

  // A day is un-pickable if it's in the past, beyond the 24-month window, or the
  // villa is already unavailable that day. Today IS pickable: admin bookings are
  // availability-gated only, so same-day walk-ins can be entered.
  const disabledDate = (current: any) => {
    if (!current) return false;
    // No upper bound. The API's 24-month ceiling is an owner self-service guard
    // that it lifts for a manager, so the panel must not re-impose it — doing so
    // blocked admin bookings the API would have accepted.
    const min = moment().startOf("day");
    if (current < min) return true;
    const ds = current.format("YYYY-MM-DD");
    return bookedDates.has(ds) || maintDates.has(ds);
  };

  // Mark every day-type with a hover tooltip: booked and maintenance (which also
  // block the date), plus peak / high peak, which stay selectable — they change
  // the rate, not availability. A blocking state wins the colour so a booked
  // peak day still reads as unavailable.
  const dateRender = (current: any) => {
    const ds = current.format("YYYY-MM-DD");
    const isMaint = maintDates.has(ds);
    const isBooked = bookedDates.has(ds);
    const special = villaDayClass(current, villaDays);
    const cls = isMaint ? "cell_maint" : isBooked ? "cell_booked" : special;
    const inner = (
      <div className={`ant-picker-cell-inner ${cls}`}>{current.date()}</div>
    );
    const title = isMaint
      ? "Maintenance day"
      : isBooked
      ? "Booked"
      : specialDayLabel(special);
    return title ? <Tooltip title={title}>{inner}</Tooltip> : inner;
  };

  const reset = () => {
    setOwner(undefined);
    setVilla(undefined);
    setStart("");
    setEnd("");
    setArrival(moment("12:00", "HH:mm"));
    setBookingFor("myself");
    setGName("");
    setGMobile("");
    setGEmail("");
    setAdults(1);
    setKids(0);
    setSeniors(0);
    setInfants(0);
    setPets(0);
    setNote("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const onSubmit = async () => {
    if (!owner || !villa || !start || !end) {
      notification.error({
        message: "Owner, villa and dates are required",
        placement: "top",
      });
      return;
    }
    if (isToday && arrival && arrival.isBefore(moment(), "minute")) {
      notification.error({
        message: "Check-in time has already passed",
        description: "Pick a later time of arrival for a same-day booking.",
        placement: "top",
      });
      return;
    }
    const body: any = {
      property_id: villa,
      owner_id: owner,
      start_date: start,
      start_time: arrival ? arrival.format("HH:mm:ss") : "12:00:00",
      end_date: end,
      end_time: "11:00:00",
      booking_for: bookingFor,
      member: {
        adults_count: adults,
        kids_count: kids,
        infants_count: infants,
        seniorcitizen_count: seniors,
        pets_count: pets,
      },
    };
    // Only send the note when there is one, so an untouched field leaves the
    // column null rather than writing an empty string.
    const trimmedNote = note.trim();
    if (trimmedNote) body.note = trimmedNote;
    if (bookingFor === "guest") {
      body.guest = {
        name: gName,
        email: gEmail,
        mobile: gMobile,
        govt_idtype: "",
      };
    }
    try {
      setSaving(true);
      const res = await API.post(APIS.BOOKING.ADD, body);
      if (res.data.status === 1) {
        notification.success({
          message: "Booking created",
          placement: "top",
        });
        reset();
        onClose();
        onCreated();
      } else {
        // clash / rule violation comes back here
        notification.error({
          message:
            res.data.data?.message ||
            res.data.message ||
            "Could not create booking",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message:
          e?.response?.data?.data?.message ||
          e?.response?.data?.message ||
          "Something went wrong",
        placement: "top",
      });
    } finally {
      setSaving(false);
    }
  };

  const lbl: any = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#6b7a72",
    margin: "0 0 6px",
  };
  const field: any = { marginBottom: 16 };

  return (
    <Modal
      visible={open}
      onCancel={close}
      title="New booking"
      footer={null}
      destroyOnClose
      width={520}
    >
      <div style={field}>
        <label style={lbl}>Owner</label>
        <Select
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="children"
          value={owner}
          placeholder="Select owner"
          onChange={(v) => setOwner(v)}
        >
          {owners.map((o: any) => (
            <Option key={o.id} value={o.id}>
              {o.first_name} {o.last_name || ""}
            </Option>
          ))}
        </Select>
      </div>

      <div style={field}>
        <label style={lbl}>Villa</label>
        <Select
          style={{ width: "100%" }}
          value={villa}
          disabled={!owner}
          placeholder={owner ? "Select villa" : "Select an owner first"}
          notFoundContent="This owner has no villas"
          onChange={(v) => setVilla(v)}
        >
          {villas.map((p: any) => (
            <Option key={p.id} value={p.id}>
              {p.title}
            </Option>
          ))}
        </Select>
      </div>

      <div style={field}>
        <label style={lbl}>Dates</label>
        <DayTypeLegend />
        <RangePicker
          style={{ width: "100%" }}
          dropdownClassName="range_fit_pop"
          disabled={!villa}
          disabledDate={disabledDate}
          dateRender={dateRender}
          value={[start ? moment(start) : null, end ? moment(end) : null]}
          onChange={(_: any, ds: any) => {
            setStart(ds?.[0] || "");
            setEnd(ds?.[1] || "");
          }}
          format="YYYY-MM-DD"
          getPopupContainer={(trigger: any) => trigger.parentElement}
        />
      </div>

      <div style={field}>
        <label style={lbl}>Time of arrival (check-in)</label>
        {(() => {
          const a = arrival || moment("12:00", "HH:mm");
          const h12 = a.hour() % 12 === 0 ? 12 : a.hour() % 12;
          const min = a.minute();
          const mer = a.hour() < 12 ? "AM" : "PM";
          // rebuild the moment from the 12-hour parts (12 AM -> 0, 12 PM -> 12)
          const at = (h: number, m: number, ap: string) => {
            let h24 = h % 12;
            if (ap === "PM") h24 += 12;
            return moment().hour(h24).minute(m).second(0);
          };
          // on a same-day booking every time already behind us is un-pickable
          const past = (h: number, m: number, ap: string) =>
            isToday && at(h, m, ap).isBefore(moment(), "minute");
          const apply = (h: number, m: number, ap: string) => {
            // changing one part can drag the others into the past (e.g. dropping
            // the hour while the minute stays) - snap back to now if it does
            setArrival(past(h, m, ap) ? moment().startOf("minute") : at(h, m, ap));
          };
          const pop = (t: any) => t.parentElement;
          return (
            <div style={{ display: "flex", gap: 8 }}>
              <Select
                style={{ flex: 1 }}
                value={h12}
                onChange={(v) => apply(Number(v), min, mer)}
                getPopupContainer={pop}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                  <Option key={h} value={h} disabled={past(h, 59, mer)}>
                    {h}
                  </Option>
                ))}
              </Select>
              <Select
                style={{ flex: 1 }}
                value={min}
                onChange={(v) => apply(h12, Number(v), mer)}
                getPopupContainer={pop}
              >
                {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                  <Option key={m} value={m} disabled={past(h12, m, mer)}>
                    {String(m).padStart(2, "0")}
                  </Option>
                ))}
              </Select>
              <Select
                style={{ width: 84 }}
                value={mer}
                onChange={(v) => apply(h12, min, v)}
                getPopupContainer={pop}
              >
                <Option value="AM" disabled={past(11, 59, "AM")}>
                  AM
                </Option>
                <Option value="PM" disabled={past(11, 59, "PM")}>
                  PM
                </Option>
              </Select>
            </div>
          );
        })()}
        <span
          style={{
            display: "block",
            marginTop: 6,
            fontSize: 12,
            color: "#8a968f",
          }}
        >
          Check-out is 11:00 AM (standard).
        </span>
      </div>

      <div style={field}>
        <label style={lbl}>Booking for</label>
        <Select
          style={{ width: "100%" }}
          value={bookingFor}
          onChange={(v) => setBookingFor(v)}
        >
          <Option value="myself">Myself (owner)</Option>
          <Option value="guest">Guest</Option>
        </Select>
      </div>

      {bookingFor === "guest" && (
        <>
          <div style={field}>
            <label style={lbl}>Guest name</label>
            <Input value={gName} onChange={(e) => setGName(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ ...field, flex: 1 }}>
              <label style={lbl}>Mobile</label>
              <Input
                value={gMobile}
                onChange={(e) => setGMobile(e.target.value)}
              />
            </div>
            <div style={{ ...field, flex: 1 }}>
              <label style={lbl}>Email</label>
              <Input
                value={gEmail}
                onChange={(e) => setGEmail(e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      <label style={lbl}>Guests</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {[
          ["Adults", adults, setAdults, 1],
          ["Kids", kids, setKids, 0],
          ["Seniors", seniors, setSeniors, 0],
          ["Infants", infants, setInfants, 0],
          ["Pets", pets, setPets, 0],
        ].map(([label, val, setter, min]: any) => (
          <div key={label} style={{ width: 88 }}>
            <label style={{ ...lbl, textTransform: "none" }}>{label}</label>
            <InputNumber
              style={{ width: "100%" }}
              min={min}
              value={val}
              onChange={(v) => setter(Number(v) || 0)}
            />
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <label style={lbl}>Note (optional)</label>
        <Input.TextArea
          rows={3}
          value={note}
          placeholder="Add a note for the owner — visible in the app"
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div
        style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 28 }}
      >
        <Button onClick={close}>Cancel</Button>
        <Button
          type="primary"
          loading={saving}
          onClick={onSubmit}
          style={{ background: "#082017", borderColor: "#082017" }}
        >
          Book a Slot
        </Button>
      </div>
    </Modal>
  );
};

export default NewBooking;
